import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import TypeAheadLookup from '../components/TypeAheadLookup';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { GROUP_ITEMS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, Upload, Image as ImageIcon, Layers } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { groupItemsConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupItemsPage() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewTab, setViewTab] = useState('analytics');
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editGroup, setEditGroup] = useState(null); // null=closed, object=editing
  const [sortState, handleSort] = useSortState('name', 'asc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/data/group-items', { params: { search, page, page_size: pageSize } });
      setGroups(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch (e) { if (e?.response?.status >= 500) toast.error('Failed to load group items'); }
    setLoading(false);
  }, [search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const handleNew = () => {
    setEditGroup({ name: '', description: '', active: true, items: [], isNew: true });
  };

  const handleRowClick = async (g) => {
    try {
      const r = await api.get(`/api/data/group-items/${g.group_id}`);
      setEditGroup({ ...r.data, isNew: false });
    } catch (e) { if (e?.response?.status >= 500) toast.error('Failed to load group'); }
  };

  const handleSave = async () => {
    if (!editGroup.name?.trim()) { toast.error('Group name is required'); return; }
    try {
      if (editGroup.isNew) {
        const r = await api.post('/api/data/group-items', editGroup);
        // Save items
        if (editGroup.items?.length > 0) {
          await api.put(`/api/data/group-items/${r.data.group_id}`, { items: editGroup.items });
        }
        toast.success('Group created');
      } else {
        await api.put(`/api/data/group-items/${editGroup.group_id}`, {
          name: editGroup.name, description: editGroup.description,
          active: editGroup.active, items: editGroup.items,
        });
        toast.success('Group saved');
      }
      setEditGroup(null);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
  };

  const handleDelete = async () => {
    if (!editGroup?.group_id || !globalConfirm('Delete this group?')) return;
    try {
      await api.delete(`/api/data/group-items/${editGroup.group_id}`);
      toast.success('Group deleted');
      setEditGroup(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const toggleFavorite = async (g) => {
    try {
      await api.put(`/api/data/group-items/${g.group_id}`, { is_favorite: !g.is_favorite });
      setGroups(prev => prev.map(x => x.group_id === g.group_id ? { ...x, is_favorite: !x.is_favorite } : x));
    } catch {}
  };

  return (
    <AppShell>
      <PageHeader title="Group Items" subtitle={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <Button onClick={handleNew} data-testid="add-group-btn"><Plus className="h-4 w-4 mr-1" /> Add New</Button> : null} />

      <div className="flex gap-4 border-b mb-4" data-testid="group-items-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="group-items-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="group-items-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Group Items
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="group-items-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...groupItemsConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={GROUP_ITEMS_REPORTS} apiPrefix="/api/module-reports/data" module="Group Items" />
      ) : (<>

      <DataTableToolbar search={search} onSearchChange={setSearch} placeholder="Search groups..." />

      {loading ? <LoadingSpinner /> : groups.length === 0 ? (
        <EmptyState title="No group items found" icon={Layers} />
      ) : (
        <table className="data-table" data-testid="group-items-table">
          <thead><tr>
            <th style={{ width: 32 }}></th>
            <SortableTableHeader label="Group Items Name" field="name" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Description" field="description" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Items" field="item_count" sortState={sortState} onSort={handleSort} className="text-center" />
            <SortableTableHeader label="Active" field="active" sortState={sortState} onSort={handleSort} className="text-center" />
          </tr></thead>
          <tbody>
            {sortData(groups, sortState.field, sortState.dir).map(g => (
              <tr key={g.group_id} className="cursor-pointer" onClick={() => handleRowClick(g)}>
                <td onClick={e => e.stopPropagation()}>
                </td>
                <td className="font-medium">{g.name}</td>
                <td className="text-muted-foreground">{g.description}</td>
                <td className="text-center"><Badge variant="outline">{(g.items || []).length}</Badge></td>
                <td className="text-center">
                  <Badge variant={g.active ? 'default' : 'secondary'}>{g.active ? 'Yes' : 'No'}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </>)}

      {/* Edit / New Modal */}
      {editGroup && (
        <GroupItemModal
          group={editGroup}
          onChange={setEditGroup}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditGroup(null)}
        />
      )}
    </AppShell>
  );
}

function GroupItemModal({ group, onChange, onSave, onDelete, onClose }) {
  const [saving, setSaving] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const imgInputRef = useRef(null);
  const [imgPreview, setImgPreview] = useState(
    group.group_id && group.image_path ? `${BACKEND_URL}/api/data/group-items/${group.group_id}/image?t=${Date.now()}` : null
  );

  const f = (k, v) => onChange(prev => ({ ...prev, [k]: v }));
  const descRemaining = 100 - (group.description || '').length;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
    if (e.target) e.target.value = '';
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5');
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) uploadImageFile(file);
  };

  const uploadImageFile = async (file) => {
    const reader = new FileReader();
    reader.onload = ev => setImgPreview(ev.target.result);
    reader.readAsDataURL(file);
    if (group.group_id) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await api.post(`/api/data/group-items/${group.group_id}/image`, fd);
        toast.success('Image uploaded');
      } catch { toast.error('Image upload failed'); }
    }
  };

  const addItemToGroup = (item) => {
    if (!item) return;
    const exists = (group.items || []).find(i => i.item_code === item.item_code);
    if (exists) { toast.error('Item already in group'); return; }
    onChange(prev => ({
      ...prev,
      items: [...(prev.items || []), { item_code: item.item_code, outline: item.description || item.outline || '', quantity: 1 }]
    }));
    setAddingItem(false);
    setItemSearch('');
  };

  const updateItemQty = (idx, qty) => {
    onChange(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, quantity: parseFloat(qty) || 0 } : it)
    }));
  };

  const removeItem = (idx) => {
    onChange(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const doSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="group-item-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{group.isNew ? 'New Group Item' : 'Edit Group Items'}</DialogTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Active</Label>
              <Switch checked={group.active} onCheckedChange={v => f('active', v)} data-testid="group-active-toggle" />
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Name + Image row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-red-500">Group Items Name *</Label>
                <Input value={group.name || ''} onChange={e => f('name', e.target.value)} maxLength={100}
                  data-testid="group-name-input" placeholder="Enter group name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-red-500">Group Items Description *</Label>
                <Textarea value={group.description || ''} onChange={e => f('description', e.target.value.slice(0, 100))}
                  rows={3} data-testid="group-desc-input" placeholder="Describe this group" />
                <p className="text-[10px] text-muted-foreground text-right">Characters remaining: {descRemaining}</p>
              </div>
            </div>
            <div>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <div
                onClick={() => imgInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary', 'bg-primary/5'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5'); }}
                onDrop={handleImageDrop}
                className="border-2 border-dashed rounded-xl h-full min-h-[160px] flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-colors"
                data-testid="group-image-upload"
              >
                {imgPreview ? (
                  <img src={imgPreview} alt="Group" className="max-h-[150px] object-contain rounded-lg" />
                ) : (
                  <>
                    <ImageIcon className="h-10 w-10 text-muted-foreground/40 mb-2" />
                    <p className="text-xs text-muted-foreground">Drag & drop image here</p>
                    <p className="text-[10px] text-muted-foreground/60">or click to browse</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Button size="sm" variant="outline" onClick={() => setAddingItem(true)} data-testid="add-item-to-group-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Items
              </Button>
              <span className="text-xs text-muted-foreground">Total Records: {(group.items || []).length}</span>
            </div>

            {addingItem && (
              <div className="mb-3 p-3 border rounded-lg bg-muted/20">
                <Label className="text-xs mb-1 block">Search items to add:</Label>
                <TypeAheadLookup
                  endpoint="/api/data/items"
                  valueField="item_code"
                  labelField="description"
                  value={itemSearch}
                  displayValue={itemSearch}
                  placeholder="Type item code or description..."
                  onChange={(code, record) => {
                    if (record) addItemToGroup(record);
                  }}
                />
                <Button size="sm" variant="ghost" className="mt-2" onClick={() => setAddingItem(false)}>
                  <X className="h-3 w-3 mr-1" /> Cancel
                </Button>
              </div>
            )}

            {(group.items || []).length > 0 ? (
              <table className="data-table w-full" data-testid="group-items-list">
                <thead><tr>
                  <th>Item Code</th>
                  <th>Outline</th>
                  <th className="text-right" style={{ width: 100 }}>Quantity</th>
                  <th style={{ width: 40 }}></th>
                </tr></thead>
                <tbody>
                  {(group.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="font-mono text-primary">{item.item_code}</td>
                      <td>{item.outline}</td>
                      <td className="text-right">
                        <Input type="number" min={1} value={item.quantity} onChange={e => updateItemQty(idx, e.target.value)}
                          className="h-7 w-20 text-right text-sm ml-auto" />
                      </td>
                      <td>
                        <button onClick={() => removeItem(idx)} className="text-destructive hover:opacity-70" aria-label="Remove item">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No items in this group yet. Click "+ Add Items" to start.</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center gap-2 pt-4">
          <Button onClick={doSave} disabled={saving} data-testid="group-save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          {!group.isNew && (
            <Button variant="destructive" onClick={onDelete} data-testid="group-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
